import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode,
} from 'react';
import themeService, { ORG_THEME_KEY, USER_THEME_KEY } from '../services/theme.service';
import { msalInstance } from '../services/authConfig';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { resolveTheme } from './resolve';
import { compileTheme } from './compile';
import type { RejectedToken, ResolvedTheme, ThemeDoc } from './types';

/**
 * The theme engine.
 *
 * Resolves the cascade (org default -> user overrides -> live draft), compiles
 * it, and keeps the result in a single <style id="theme-engine"> element at the
 * end of <head>.
 *
 * Why a stylesheet and not inline styles on <html>: inline styles beat every
 * stylesheet rule, so writing tokens inline is how you accidentally defeat
 * things like the .dark.theme-grey block - which is exactly what
 * DisplayPreferencesContext does today. A stylesheet appended last wins on
 * source order at equal specificity and stays overridable by anything more
 * specific.
 */

export { ORG_THEME_KEY, USER_THEME_KEY };
export const STYLE_ELEMENT_ID = 'theme-engine';

/**
 * Pre-paint cache. Read by the bootstrap script in index.html BEFORE React (and
 * before MSAL) exists, so the key cannot be namespaced by email - the owner is
 * recorded inside the blob instead and validated once the user is known. A
 * mismatch means a different person is on this browser: their theme is dropped.
 */
export const BOOTSTRAP_CACHE_KEY = 'theme_bootstrap';

/** Sign-in watch: 30s is ample for a no-reload sign-in, and stops for guards. */
export const AUTH_POLL_INTERVAL_MS = 1000;
export const AUTH_POLL_ATTEMPTS = 30;

interface BootstrapCache {
  email: string | null;
  css: string;
}

/** Put the compiled CSS into the document, creating or removing the element. */
export function applyThemeCss(css: string): void {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;

  if (!css) {
    existing?.remove();
    return;
  }

  const el = existing ?? document.createElement('style');
  if (!existing) {
    el.id = STYLE_ELEMENT_ID;
    document.head.appendChild(el);
  } else if (el.nextElementSibling) {
    // Something was appended after us (Vite injects styles in dev). Move back to
    // the end so the theme still wins ties on source order.
    document.head.appendChild(el);
  }
  if (el.textContent !== css) el.textContent = css;
}

function readBootstrapCache(): BootstrapCache | null {
  try {
    const raw = localStorage.getItem(BOOTSTRAP_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.css !== 'string') return null;
    return { email: typeof parsed.email === 'string' ? parsed.email : null, css: parsed.css };
  } catch {
    return null;      // private mode or a corrupt blob - defaults apply
  }
}

function writeBootstrapCache(cache: BootstrapCache): void {
  try {
    localStorage.setItem(BOOTSTRAP_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* storage full or unavailable - the theme still applies this session */
  }
}

function clearBootstrapCache(): void {
  try {
    localStorage.removeItem(BOOTSTRAP_CACHE_KEY);
  } catch {
    /* nothing to do */
  }
}

/** Signed-in account email, or null when nobody is signed in. */
function currentEmail(): string | null {
  try {
    const accounts = msalInstance.getAllAccounts();
    return accounts?.length ? accounts[0].username : null;
  } catch {
    return null;
  }
}

/**
 * Impersonation state without a hard dependency on the provider.
 *
 * useImpersonation() throws when its provider is missing. This provider sits
 * inside it today, but coupling a theme concern to that ordering is the kind of
 * fragility that breaks a page for everyone if the tree is ever rearranged. The
 * hook is still called unconditionally, so hook order is stable.
 */
function useIsImpersonating(): boolean {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return Boolean(useImpersonation().impersonationStatus?.is_impersonating);
  } catch {
    return false;
  }
}

interface ThemeEngineContextvalue {
  /** The merged, validated theme currently applied. */
  resolved: ResolvedTheme;
  /** The org document as loaded (null until it arrives). */
  orgTheme: ThemeDoc | null;
  /** This user's overrides. */
  userTheme: ThemeDoc | null;
  /** The unsaved edit being previewed across the whole session. */
  draft: ThemeDoc | null;
  setDraft: (doc: ThemeDoc | null) => void;
  /** Persist the draft (or an explicit doc) as this user's theme. */
  saveUserTheme: (doc?: ThemeDoc) => Promise<void>;
  /** Publish the draft (or an explicit doc) as the org default, for everyone. */
  publishOrgTheme: (doc?: ThemeDoc) => Promise<void>;
  /** Values the resolver refused, so the UI can explain itself. */
  rejected: RejectedToken[];
  isLoading: boolean;
  /** Whether a draft repaints the whole app, or only where the page scopes it. */
  applyDraftGlobally: boolean;
  setApplyDraftGlobally: (on: boolean) => void;
}

const EMPTY_RESOLVED: ResolvedTheme = {
  enabled: false,
  tokens: { light: {}, dark: {} },
  components: { light: {}, dark: {} },
  typography: {},
  layout: {},
};

const defaultValue: ThemeEngineContextvalue = {
  resolved: EMPTY_RESOLVED,
  orgTheme: null,
  userTheme: null,
  draft: null,
  setDraft: () => {},
  saveUserTheme: async () => {},
  publishOrgTheme: async () => {},
  rejected: [],
  isLoading: false,
  applyDraftGlobally: true,
  setApplyDraftGlobally: () => {},
};

const ThemeEngineContext = createContext<ThemeEngineContextvalue>(defaultValue);

export const ThemeEngineProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [orgTheme, setOrgTheme] = useState<ThemeDoc | null>(null);
  const [userTheme, setUserTheme] = useState<ThemeDoc | null>(null);
  const [draft, setDraft] = useState<ThemeDoc | null>(null);
  // Whether a draft repaints the whole app. Off confines it to whatever the
  // Customize page scopes it to, so the surrounding UI holds still while an
  // author tunes one value at a time.
  const [applyDraftGlobally, setApplyDraftGlobally] = useState(true);
  /** Bumped when sign-in is detected, to re-run the load. */
  const [authTick, setAuthTick] = useState(0);
  const impersonating = useIsImpersonating();
  const [isLoading, setIsLoading] = useState(true);
  const bootstrapValidated = useRef(false);

  const { theme: resolved, rejected } = useMemo(() => {
    const result = resolveTheme(orgTheme, userTheme, draft);
    if (!draft) return result;

    // A DRAFT always renders, even when org theming is switched off.
    //
    // `enabled` fails closed and is read only from the org document, which ships
    // as false - so without this an admin building the very first theme, or any
    // user editing their own, drags every control and watches nothing happen.
    //
    // This does not weaken the switch. A draft exists only in this browser and
    // is never persisted, so previewing one cannot put a theme in front of
    // anybody else. What OTHER people get is still decided entirely by the saved
    // org document, and `savedCompiled` below - the thing that gets cached for
    // the next load - deliberately resolves without the draft.
    return { ...result, theme: { ...result.theme, enabled: true } };
  }, [orgTheme, userTheme, draft]);

  const compiled = useMemo(() => compileTheme(resolved), [resolved]);

  // Drop a cached theme belonging to somebody else before it can be trusted.
  // The bootstrap script applies the cache blind (it runs before MSAL), so this
  // is the first point where the owner can actually be checked.
  useEffect(() => {
    if (bootstrapValidated.current) return;
    bootstrapValidated.current = true;
    const cache = readBootstrapCache();
    if (!cache) return;
    const email = currentEmail();
    if (email && cache.email && cache.email !== email) {
      clearBootstrapCache();
      applyThemeCss('');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Do not touch the API until somebody is signed in.
    //
    // This provider wraps the router including /login, so it mounts on the login
    // page. A settings read there goes out without a token, 401s, and trips the
    // interceptor's redirect to /login - which remounts this provider and does it
    // again. That was a production login loop. The service guards each call too;
    // this is the second layer, and it also spares the login page two pointless
    // requests.
    //
    // MSAL uses a redirect flow, so a successful login reloads the document and
    // this provider mounts fresh with an account present. The poll is a cheap
    // safety net for any path that signs in without a reload.
    if (!currentEmail()) {
      setIsLoading(false);
      // BOUNDED. An unbounded poll would run forever for anyone who never gets
      // an MSAL account - which includes the whole guard /yard tree, where users
      // authenticate with a guard JWT instead and this provider still wraps
      // their routes. That is a permanent 1s timer on yard tablets for nothing.
      // A real sign-in uses MSAL's redirect flow and reloads the document, so
      // this is only a safety net for a sign-in that happens without a reload.
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        if (currentEmail()) {
          clearInterval(timer);
          setAuthTick((n) => n + 1);
        } else if (attempts >= AUTH_POLL_ATTEMPTS) {
          clearInterval(timer);
        }
      }, AUTH_POLL_INTERVAL_MS);
      return () => { cancelled = true; clearInterval(timer); };
    }

    (async () => {
      try {
        // The org default is readable by any authenticated user; the per-user
        // document only exists once they have customised something. Both reads
        // swallow their own failures, so a theming outage leaves the app stock.
        const [org, user] = await Promise.all([
          themeService.getOrgTheme(),
          themeService.getUserTheme(),
        ]);
        if (cancelled) return;

        setOrgTheme(org);
        setUserTheme(user);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [authTick]);

  // What a fresh load should paint, and what the app shows when a draft is not
  // being applied globally: the SAVED cascade, resolved without the draft.
  const savedCompiled = useMemo(
    () => compileTheme(resolveTheme(orgTheme, userTheme).theme),
    [orgTheme, userTheme],
  );

  // Apply on every change so a draft edit shows up across the whole app
  // immediately - but NOT while the initial load is still in flight. On mount
  // nothing is loaded, the resolved theme is empty, and applying that '' would
  // REMOVE the stylesheet the bootstrap script injected before first paint:
  // a themed user would flash to stock on every load and snap back when the
  // API landed, defeating the entire pre-paint mechanism. Hold the bootstrap
  // CSS until real data replaces it. (A draft cannot exist before loading
  // finishes, but the guard keeps that assumption out of the correctness
  // argument.)
  useEffect(() => {
    if (isLoading && !draft) return;
    applyThemeCss(applyDraftGlobally ? compiled : savedCompiled);
  }, [compiled, savedCompiled, applyDraftGlobally, isLoading, draft]);

  // Cache what a fresh load should paint before React exists. Only the SAVED
  // cascade is cached - a draft is deliberately not, or an abandoned experiment
  // would come back on the next load.
  useEffect(() => {
    if (isLoading) return;
    if (!savedCompiled) {
      clearBootstrapCache();
      return;
    }
    // Never cache while impersonating. Settings reads resolve to the impersonated
    // user server-side - which is right, an admin should see what they see - but
    // currentEmail() is the ADMIN's. Caching that pair stamps someone else's
    // theme with the admin's name, and the admin's next load paints it pre-paint
    // until the API corrects it.
    if (impersonating) return;
    writeBootstrapCache({ email: currentEmail(), css: savedCompiled });
  }, [savedCompiled, isLoading, impersonating]);

  const saveUserTheme = useCallback(async (doc?: ThemeDoc) => {
    const next = doc ?? draft;
    if (!next) return;
    await themeService.saveUserTheme(next);
    setUserTheme(next);
    setDraft(null);
  }, [draft]);

  const publishOrgTheme = useCallback(async (doc?: ThemeDoc) => {
    const next = doc ?? draft;
    if (!next) return;
    await themeService.publishOrgTheme(next);
    setOrgTheme(next);
    setDraft(null);
  }, [draft]);

  const value = useMemo<ThemeEngineContextvalue>(() => ({
    resolved,
    orgTheme,
    userTheme,
    draft,
    setDraft,
    saveUserTheme,
    publishOrgTheme,
    rejected,
    isLoading,
    applyDraftGlobally,
    setApplyDraftGlobally,
  }), [resolved, orgTheme, userTheme, draft, saveUserTheme, publishOrgTheme, rejected,
    isLoading, applyDraftGlobally]);

  return <ThemeEngineContext.Provider value={value}>{children}</ThemeEngineContext.Provider>;
};

/**
 * Safe outside a provider: returns inert defaults rather than throwing, matching
 * how useTheme behaves, so a component rendered in isolation still works.
 */
export const useThemeEngine = (): ThemeEngineContextvalue => useContext(ThemeEngineContext);
